import { useEffect, useState } from 'react'
import { ExhibitStatus } from '@prisma/client'
import { CaretForward, CaretDown } from 'react-ionicons'
import { WorkListItem } from './WorkListItem'
import { MediaPlayerListItem } from './MediaPlayerListItem'
import {
  useExhibitions, useWorks, reorderWorks, ExhibitionWithWorks, useMediaPlayers
} from '../lib/apiClient'
import { SortableList } from './SortableList'
import ExhibitionWithSortableWorks from './ExhibitionWithSortableWorks'
import { logger } from '../lib/logger'

export const AdminDashboard: React.FC = () => {
  const {
    data: exhibitions,
    isLoading,
    isError,
    mutate,
  } = useExhibitions(true)

  const { data: orphanWorks, mutate: mutateOrphanWorks } = useWorks({
    exhibitionId: null,
    includeInactive: true,
    includePlayers: true,
    includeTracks: true,
  })

  const [currentEx, setCurrentEx] = useState<Array<ExhibitionWithWorks>>([])
  const [upcomingEx, setUpcomingEx] = useState<Array<ExhibitionWithWorks>>([])
  const [archivedEx, setArchivedEx] = useState<Array<ExhibitionWithWorks>>([])
  useEffect(() => {
    if (!isError && !isLoading) {
      const current = []
      const upcoming = []
      const archived = []
      for (const e of exhibitions) {
        if (e.showStatus === ExhibitStatus.Current) current.push(e)
        else if (e.showStatus === ExhibitStatus.Upcoming) upcoming.push(e)
        else if (e.showStatus === ExhibitStatus.Archived) archived.push(e)
      }
      setCurrentEx(current)
      setUpcomingEx(upcoming)
      setArchivedEx(archived)
    }
  }, [exhibitions, isError, isLoading])

  const [archivedVisible, setArchivedVisibility] = useState<boolean>(false)

  useEffect(() => {
    // this is necessary so changes will be reflected when works are deleted
    mutate()
    mutateOrphanWorks()
  }, [mutate, mutateOrphanWorks])

  const { data: orphanMediaPlayers } = useMediaPlayers({ workId: null })

  const onOrphanChange = async (update) => {
    await reorderWorks(update.map((w) => w.id))
    mutateOrphanWorks(update)
  }
  const onExhibitionWithWorksChange = async (update) => {
    await reorderWorks(update.map((w) => w.id))
    mutate()
  }
  return (
    <div>
      {isLoading && <p>Loading...</p>}
      {isError && <p>Error!</p>}

      {exhibitions?.length === 0 && <p>No current exhibitions.</p>}

      {upcomingEx?.length ? (
        <>
          <h2 className="showStatusHeader">Upcoming Exhibitions</h2>
          <div className="exhibitContainer">
            { upcomingEx?.map((e: ExhibitionWithWorks) => (
              <ExhibitionWithSortableWorks e={e} onExhibitionWithWorksChange={onExhibitionWithWorksChange} key={e.name} />
            ))}
          </div>
        </>

      ) : ''}

      {currentEx?.length ? (
        <>
          <h2 className="showStatusHeader">Current Exhibitions</h2>
          <div className="exhibitContainer">
            { currentEx?.map((e: ExhibitionWithWorks) => (
              <ExhibitionWithSortableWorks e={e} onExhibitionWithWorksChange={onExhibitionWithWorksChange} key={e.name} />
            ))}
          </div>
        </>

      ) : ''}
      {archivedEx?.length ? (
        <>
          <div className="flex">
            <h2 className="showStatusHeader">Archived Exhibitions</h2>
            <button
              type="button"
              className="caretBtn"
              onClick={() => { setArchivedVisibility(!archivedVisible) }}
              onKeyDown={() => { logger.info('Show/Hide Archived Exhibition Works') }}
            >
              { archivedVisible ? <CaretDown /> : <CaretForward />}
            </button>
          </div>
          <div className="exhibitContainer">
            {archivedVisible ? archivedEx?.map((e: ExhibitionWithWorks) => (
              <ExhibitionWithSortableWorks e={e} onExhibitionWithWorksChange={onExhibitionWithWorksChange} key={e.name} />
            )) : ''}
          </div>
        </>

      ) : ''}

      {!!orphanWorks?.length && (
      <>
        <h4>Works not assigned an exhibition:</h4>
        <SortableList
          items={orphanWorks}
          onChange={onOrphanChange}
          renderItem={(item) => (
            <SortableList.Item id={item.id}>
              <SortableList.DragHandle />
              <WorkListItem adminView settingsLink work={item} />
            </SortableList.Item>
          )}
        />
      </>
      )}
      {!!orphanMediaPlayers?.length && <h4>Unassigned media players:</h4>}
      <div className="inline-media-player-list">
        {
          orphanMediaPlayers?.map((mp) => (
            <MediaPlayerListItem key={mp.id} mediaPlayer={mp} />
          ))
        }
      </div>
    </div>
  )
}
